import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileText, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface UploadedFile {
  file: File;
  title: string;
  content: string;
}

const DocumentUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: UploadedFile[] = [];
    
    for (const file of Array.from(files)) {
      const isSupported = 
        file.type === 'text/plain' ||
        file.type === 'application/pdf' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword' ||
        file.name.endsWith('.txt') ||
        file.name.endsWith('.pdf') ||
        file.name.endsWith('.docx') ||
        file.name.endsWith('.doc');

      if (isSupported) {
        try {
          let content = '';
          
          if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
            // For text files, read as text
            content = await file.text();
          } else {
            // For binary files (PDF, DOCX), read as base64
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            const base64String = btoa(String.fromCharCode(...uint8Array));
            content = base64String;
          }
          
          newFiles.push({
            file,
            title: file.name.replace(/\.[^/.]+$/, ""), // Remove file extension
            content: content.substring(0, 8000) // For display purposes only
          });
        } catch (error) {
          toast({
            title: "Error reading file",
            description: `Could not read ${file.name}`,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Unsupported file type",
          description: `${file.name} is not a supported file type. Supported formats: .txt, .pdf, .docx, .doc`,
          variant: "destructive",
        });
      }
    }

    setSelectedFiles(prev => [...prev, ...newFiles]);
    // Clear the input
    event.target.value = '';
  };

  const updateFileTitle = (index: number, newTitle: string) => {
    setSelectedFiles(prev => prev.map((file, i) => 
      i === index ? { ...file, title: newTitle } : file
    ));
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one file to upload.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      for (const fileData of selectedFiles) {
        // Get the full content including base64 for binary files
        let fullContent = fileData.content;
        
        // If it's not a text file, read the full file as base64
        if (fileData.file.type !== 'text/plain' && !fileData.file.name.endsWith('.txt')) {
          const arrayBuffer = await fileData.file.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          fullContent = btoa(String.fromCharCode(...uint8Array));
        }

        const { data, error } = await supabase.functions.invoke('process-knowledge-document', {
          body: {
            title: fileData.title,
            content: fullContent,
            fileName: fileData.file.name,
            fileType: fileData.file.type,
            fileSize: fileData.file.size,
          }
        });

        if (error) {
          throw new Error(error.message);
        }

        console.log('Document processed:', data);
      }

      toast({
        title: "Upload successful",
        description: `${selectedFiles.length} document(s) uploaded and queued for processing.`,
      });

      setSelectedFiles([]);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Document Upload
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="file-upload">Select Documents</Label>
          <div className="flex items-center gap-2">
            <Input
              id="file-upload"
              type="file"
              multiple
              accept=".txt,.pdf,.docx,.doc,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              onChange={handleFileSelect}
              className="flex-1"
            />
            <Button 
              onClick={() => document.getElementById('file-upload')?.click()}
              variant="outline"  
              size="sm"
            >
              <Upload className="h-4 w-4 mr-2" />
              Browse
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Supported formats: .txt, .pdf, .docx, .doc files (content will be extracted automatically)
          </p>
        </div>

        {selectedFiles.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Selected Files ({selectedFiles.length})</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {selectedFiles.map((fileData, index) => (
                <div key={index} className="flex items-center gap-2 p-3 border rounded-lg">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Input
                      value={fileData.title}
                      onChange={(e) => updateFileTitle(index, e.target.value)}
                      placeholder="Document title"
                      className="h-8"
                    />
                    <p className="text-xs text-muted-foreground">
                      {fileData.file.name} • {(fileData.file.size / 1024).toFixed(1)}KB
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeFile(index)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button 
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || isUploading}
            className="flex-1"
          >
            {isUploading ? "Uploading..." : `Upload ${selectedFiles.length || ''} Document${selectedFiles.length === 1 ? '' : 's'}`}
          </Button>
          {selectedFiles.length > 0 && (
            <Button 
              variant="outline" 
              onClick={() => setSelectedFiles([])}
            >
              Clear All
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentUpload;